/**
 * MotivationalNavDots — Question Navigation Indicators
 *
 * Horizontal row of numbered dots showing completion status for each question.
 * Clickable for direct navigation. Highlights the current question.
 *
 * @module copilot/widgets/motivational-form
 */

import { cn } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import { CheckCircle2 } from '@tonnie/assets';

interface MotivationalNavDotsProps {
  /** Total number of questions */
  total: number;
  /** 0-based index of the current question */
  currentIndex: number;
  /** Which questions have non-empty answers */
  answeredSet: Set<number>;
  /** Which questions have validation errors */
  errorSet: Set<number>;
  /** Whether the user is on the review screen */
  isReview: boolean;
  /** Navigate to a specific question index */
  onNavigate: (index: number) => void;
}

export function MotivationalNavDots({
  total,
  currentIndex,
  answeredSet,
  errorSet,
  isReview,
  onNavigate,
}: MotivationalNavDotsProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="widget-motivational_form-nav">
      {Array.from({ length: total }, (_, idx) => {
        const isActive = !isReview && idx === currentIndex;
        const isAnswered = answeredSet.has(idx);
        const hasError = errorSet.has(idx);

        return (
          <button
            key={idx}
            type="button"
            onClick={() => onNavigate(idx)}
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 shrink-0',
              'hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              isActive
                ? 'bg-primary text-primary-foreground shadow-md scale-110'
                : hasError
                  ? 'bg-destructive/10 text-destructive-accessible border border-destructive/30'
                  : isAnswered
                    ? 'bg-success/10 text-success-accessible border border-success/30'
                    : 'bg-muted text-muted-foreground border border-border/50'
            )}
            title={t('checkup.motivational.questionNumber', { number: idx + 1 })}
          >
            {isAnswered && !hasError && !isActive ? (
              <CheckCircle2 className="w-3.5 h-3.5" />
            ) : (
              idx + 1
            )}
          </button>
        );
      })}

      {/* Review dot */}
      <div className="mx-1 h-px w-3 bg-border" />
      <button
        type="button"
        onClick={() => onNavigate(-1)} // -1 = review
        className={cn(
          'h-7 px-2.5 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200 shrink-0',
          'hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          isReview
            ? 'bg-primary text-primary-foreground shadow-md'
            : 'bg-muted text-muted-foreground border border-border/50'
        )}
        title={t('common.review')}
      >
        {t('common.review')}
      </button>
    </div>
  );
}
